import { type ComponentProps, createElement } from 'react';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@monorepo/nova-ui';
import { alpha, styled } from '@mui/material/styles';

const creditModalBoxSx = {
  boxShadow: 'none',
  backgroundColor: 'transparent',
  padding: 0,
} as const;

type CreditModalProps = ComponentProps<typeof Modal>;

const CreditModalWithBoxDefaults = ({ boxProps, ...props }: CreditModalProps) =>
  createElement(Modal, {
    ...props,
    boxProps: {
      ...boxProps,
      sx: [
        creditModalBoxSx,
        ...(Array.isArray(boxProps?.sx)
          ? boxProps.sx
          : boxProps?.sx != null
            ? [boxProps.sx]
            : []),
      ],
    },
  });

export const StyledCreditModal = styled(CreditModalWithBoxDefaults)(() => ({}));

export const StyledCreditModalSurface = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isExpiringCredit',
})<{ isExpiringCredit?: boolean }>(({ theme, isExpiringCredit }) => {
  const cornerGlow = theme.palette.secondary.main;
  const surfaceBase =
    theme.palette.mode === 'light'
      ? theme.palette.common.white
      : theme.palette.common.black;
  const ambientLime =
    theme.palette.mode === 'light'
      ? theme.palette.secondary.main
      : theme.palette.primary.main;

  const background = isExpiringCredit
    ? surfaceBase
    : `radial-gradient(ellipse 80% 70% at 0% 0%, ${alpha(
        cornerGlow,
        0.6,
      )} 0%, transparent 55%), ${surfaceBase}`;

  return {
    position: 'relative',
    margin: theme.spacing(-12),
    padding: theme.spacing(12),
    maxWidth: theme.spacing(163),
    borderRadius: theme.customShape.borderRadius5XL,
    alignItems: 'center',
    background,
    boxShadow: `0 ${theme.spacing(16)} ${theme.spacing(32)} 0 ${alpha(
      ambientLime,
      0.2,
    )}`,

    [theme.breakpoints.down('md')]: {
      maxWidth: `min(100vw - ${theme.spacing(4)}, ${theme.spacing(163)})`,
      margin: theme.spacing(-6),
      padding: theme.spacing(8, 6),
    },
  };
});

export const StyledCreditModalClose = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(6),
  right: theme.spacing(6),
  padding: theme.spacing(1),

  [theme.breakpoints.down('md')]: {
    top: theme.spacing(4),
    right: theme.spacing(4),
  },
}));

export const StyledCreditContentStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  width: '100%',
  maxWidth: theme.spacing(139),
  gap: theme.spacing(4),
}));

export const StyledCreditsIconWrap = styled(Box)(({ theme }) => ({
  width: theme.spacing(30),
  height: theme.spacing(30),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& .MuiSvgIcon-root': {
    fontSize: theme.spacing(30),
  },
}));

export const StyledCreditTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightBold,
  textAlign: 'center',
  lineHeight: 1,

  [theme.breakpoints.down('sm')]: {
    ...theme.typography.h4,
  },
}));

export const StyledCreditTitleLine = styled('span')(() => ({
  display: 'block',
}));

export const StyledCreditBodyGroup = styled(Stack)(() => ({
  width: '100%',
  alignItems: 'center',
  gap: 0,
  '& .MuiTypography-root': {
    margin: 0,
  },
}));

export const StyledCreditEmphasis = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(36),
  lineHeight: 1,
  fontStyle: 'italic',
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.primary,
  textAlign: 'center',
  letterSpacing: theme.typography.pxToRem(0.25),

  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(24),
  },
}));

export const StyledCreditBody = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(18),
  lineHeight: 1.4,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  letterSpacing: theme.typography.pxToRem(0.15),
}));

export const StyledCreditFootnote = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(18),
  lineHeight: 1.4,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  letterSpacing: theme.typography.pxToRem(0.15),
}));

export const StyledPrimaryCreditButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.customShape.borderRadiusFull,
  minHeight: theme.spacing(12),
  paddingLeft: theme.spacing(7),
  paddingRight: theme.spacing(7),
  fontSize: theme.typography.pxToRem(18),
  lineHeight: 1.4,
  fontWeight: theme.typography.fontWeightMedium,
  letterSpacing: theme.typography.pxToRem(0.15),
}));

export const StyledSecondaryCreditButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.customShape.borderRadiusFull,
  minHeight: theme.spacing(8),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  letterSpacing: theme.typography.pxToRem(0.5),
  color: theme.palette.primary.main,
}));

export const StyledAssetSelectorPanel = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.text.secondary}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: `0 ${theme.typography.pxToRem(1.25)} ${theme.typography.pxToRem(4)} 0 rgba(0,0,0,0.25)`,
  padding: theme.spacing(4),
  width: theme.spacing(74),
  gap: theme.spacing(4),
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    width: `min(100%, ${theme.spacing(74)})`,
  },
}));

export const StyledAssetSelectorTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
  textAlign: 'center',
}));

interface StyledCreditAssetCardsStackProps {
  isScrollable?: boolean;
}

export const StyledCreditAssetCardsStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isScrollable',
})<StyledCreditAssetCardsStackProps>(({ theme, isScrollable }) => ({
  width: '100%',
  gap: theme.spacing(1),
  ...(isScrollable && {
    maxHeight: 'min(35vh, 360px)',
    overflowY: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  }),
}));

interface StyledAssetCardProps {
  selected?: boolean;
}

export const StyledAssetCard = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<StyledAssetCardProps>(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.background.paper : 'transparent',
  border: `1px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
  borderRadius: theme.customShape.borderRadiusLarge,
  padding: theme.spacing(3, 4),
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  width: '100%',
  transition: theme.transitions.create(['background-color', 'border-color']),
}));

export const StyledAssetCardLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
}));

export const StyledAssetCardRow = styled(Stack)(() => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}));

export const StyledAssetCardRowLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
}));

export const StyledAssetCardRowValue = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
}));

export const StyledNewOrderLink = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.primary.main,
  textAlign: 'center',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const StyledApplyOnCartButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.customShape.borderRadiusFull,
  minHeight: theme.spacing(8),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
}));

export const StyledAssetSelectorMobileHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(4),
  padding: theme.spacing(2, 4),
  backgroundColor: theme.palette.primary.dark,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

export const StyledAssetSelectorMobileTitle = styled(Typography)(
  ({ theme }) => ({
    ...theme.typography.subtitle2,
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightMedium,
  }),
);

export const StyledAssetSelectorMobileApplyButton = styled(Button)(
  ({ theme }) => ({
    borderRadius: theme.customShape.borderRadiusFull,
    minHeight: theme.spacing(8),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
    flexShrink: 0,
    '&:hover': {
      borderColor: theme.palette.common.white,
    },
  }),
);
